import { useRef, useId } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useFocusTrap } from '../hooks/useFocusTrap';
import type { ThemeMode } from '../types/theme';

interface UserMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  /** Role label for the second line under the email (e.g. "Customer · Acme Inc"). */
  roleLabel?: string;
  onSignOut: () => void;
}

/**
 * Drawer triggered from the Header user-menu. Replaces the dedicated
 * /settings page. Theme picker + account links + sign out.
 *
 * Q5 (migration map): focus-trapped, ESC-closes, dialog ARIA.
 */
export default function UserMenuDrawer({
  open,
  onClose,
  userEmail,
  roleLabel,
  onSignOut,
}: UserMenuDrawerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const { theme, setTheme } = useTheme();
  useFocusTrap(ref, open, onClose);

  if (!open) return null;

  const themeOpts: { value: ThemeMode; label: string }[] = [
    { value: 'system', label: 'System' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ];

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-base-content/30"
        onClick={onClose}
        aria-label="Close menu"
        tabIndex={-1}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed right-4 top-16 z-50 flex w-80 flex-col gap-4 rounded-box border border-base-300 bg-base-100 p-6 shadow-lg"
      >
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <p
              id={titleId}
              className="text-base font-semibold text-base-content"
            >
              {userEmail}
            </p>
            {roleLabel && (
              <p className="text-xs text-base-content/60">{roleLabel}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-lg text-base-content/70 hover:text-base-content"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <div className="h-px w-full bg-base-300" />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-base-content">
            Theme
          </legend>
          <div className="flex gap-2">
            {themeOpts.map((opt) => {
              const active = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  className={`flex-1 rounded-btn border px-3 py-1.5 text-sm font-medium ${
                    active
                      ? 'border-primary border-2 bg-base-200 text-base-content'
                      : 'border-base-300 bg-base-200 text-base-content/80 hover:text-base-content'
                  }`}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="h-px w-full bg-base-300" />

        <nav aria-label="Account" className="flex flex-col gap-1">
          <a
            href="#account"
            className="rounded-btn px-3 py-2 text-sm font-medium text-base-content hover:bg-base-200"
          >
            Account details
          </a>
          <a
            href="#notifications"
            className="rounded-btn px-3 py-2 text-sm font-medium text-base-content hover:bg-base-200"
          >
            Notifications
          </a>
          <a
            href="#help"
            className="rounded-btn px-3 py-2 text-sm font-medium text-base-content hover:bg-base-200"
          >
            Help &amp; support
          </a>
        </nav>

        <div className="h-px w-full bg-base-300" />

        <button
          type="button"
          onClick={onSignOut}
          className="btn btn-error w-full"
        >
          Sign out
        </button>
      </div>
    </>
  );
}
